// Global
import { MouseEvent, SetStateAction, useEffect, useRef, useState } from 'react';
import { ArticleCard } from '@sitecore-search/ui';
import { WidgetComponentProps } from '@sitecore-search/react/dist/esm/types';
import type {
  ActionProp,
  ItemClickedAction,
  SearchResponseFacet,
  SearchResponseFacetItem,
  SearchResultsInitialState,
  SearchResultsStoreState,
} from '@sitecore-search/react';
import {
  FacetChangedPayload,
  WidgetDataType,
  useSearchResultsActions,
  widget,
} from '@sitecore-search/react';
import Select, { GroupBase, OptionsOrGroups } from 'react-select';

// Lib
import { Profiles } from 'lib/templates/Feature.BOKF.model';
import { ComponentProps } from 'lib/component-props';
import { useDictionary } from 'lib/hooks/use-dictionary';
import { FacetValue, urlToFacet, useEnsureFacetUrl } from 'lib/utils/use-ensure-facet-url';

// Local
import Button from 'helpers/Button/Button';
import SVG from 'helpers/SVG/SVG';
import classNames from 'classnames';
import { useSanitizedSearchResults } from 'lib/utils/token-replacer';
import { Spinner } from 'components/search';

export type BankerSearchProps = Profiles.DataTypes.BankerSearch &
  ComponentProps &
  WidgetComponentProps;

type ArticleCardItemCardProps = {
  className?: string;
  displayText?: boolean;
  description?: string;
  article: ArticleModel;
  onItemClick: ActionProp<ItemClickedAction>;
  index: number;
};

type SearchFacetsProps = {
  facets: SearchResponseFacet[];
  props?: BankerSearchProps;
  state?: SetStateAction<string | undefined>;
  setShowSearchResults: (value: boolean) => void;
};

const ArticleHorizontalItemCard = ({ article, onItemClick, index }: ArticleCardItemCardProps) => {
  const dict = useDictionary();
  const dictNMLS = dict?.['Feature.Profiles.BankerProfileNMLS'] || 'NMLS #';

  return (
    <ArticleCard.Root key={article.id}>
      <a
        className="group flex flex-row p-4 gap-6 flex-nowrap w-full relative border rounded hover:border-light-stroke-primary-variant hover:shadow-small text-light-fill-primary hover:text-light-fill-primary hover:no-underline bg-light-background-neutral-variant"
        href={article.url}
        onClick={() => {
          onItemClick({
            id: article.id,
            index,
            sourceId: article.source_id,
          });
        }}
      >
        <div className="max-w-[104px] max-h-[176px] md:max-w-[200px] md:max-h-[248px] min-w-[104px] min-h-[176px] md:min-w-[200px] md:min-h-[248px]">
          <ArticleCard.Image
            src={article?.image_url}
            className="h-full w-full object-cover object-center lg:h-full lg:w-full"
          />
        </div>
        <ArticleCard.ArticleCardContent className="p2 flex flex-col justify-between w-full">
          <div>
            <ArticleCard.Title className="s2-medium mb-2 break-words">
              {article.name}
            </ArticleCard.Title>
            <ArticleCard.Subtitle className="p2 break-words text-light-fill-primary-variant">
              {article.job_title}
            </ArticleCard.Subtitle>
            <div className="c1 text-light-fill-primary-variant pb-2 mt-4">
              {dictNMLS}
              {article.nmls}
            </div>
            <div className="c1 text-light-fill-primary-variant pb-2 break-all">{article.email}</div>
            <address className="c1 text-light-fill-primary-variant not-italic break-words">
              {article.address}
              <br />
              {article.city}, {article.state}&nbsp;{article.zipcode}
            </address>
          </div>
          <div className="flex justify-end w-full mt-1">
            <SVG
              svg="icon-arrow-right"
              hidden={false}
              className="[&_svg]:w-5 [&_svg]:h-5 text-light-fill-secondary block"
            ></SVG>
          </div>
        </ArticleCard.ArticleCardContent>
      </a>
    </ArticleCard.Root>
  );
};

export const SearchFacetsServiceLocations = ({
  facets,
  props,
  setShowSearchResults,
}: SearchFacetsProps) => {
  const { onFacetClick } = useSearchResultsActions();
  const { StateSelectText, StateLabel, CitySelectText, CityLabel, SearchLabel } =
    props?.fields || {};

  const [serviceState, setServiceState] = useState<string>(StateSelectText?.value || '');
  const [serviceCity, setServiceCity] = useState<string>(CitySelectText?.value || '');

  const [selectCityDisabled, setSelectCityDisabled] = useState<boolean>(true);
  const [cityFacet, setCityFacet] = useState<FacetChangedPayload>();

  const [cityFacetValidation, setCityFacetValidation] = useState<boolean>(false);
  const [stateFacetValidation, setStateFacetValidation] = useState<boolean>(false);

  const [cityKey, setCityKey] = useState(0);

  const getServiceCities = (array: SearchResponseFacet[], name: string, label: string) => {
    const facetServiceCities = array.filter((a) => a.name === name).map((a) => a.value);
    function removeCityItemsWithSlash(array: SearchResponseFacetItem[]) {
      return array?.filter((item) => {
        return Object.values(item).some(
          (value) => typeof value === 'string' && value.includes(serviceState + '/')
        );
      });
    }
    const serviceCities = removeCityItemsWithSlash(facetServiceCities[0]);

    // Sort cities: items with "All Cities" come first, then alphabetically
    const sortedCities = serviceCities?.toSorted((a, b) => {
      const aText = a.text.split('/')[1] || '';
      const bText = b.text.split('/')[1] || '';
      const aHasAllCities = aText.includes('All Cities');
      const bHasAllCities = bText.includes('All Cities');

      if (aHasAllCities && !bHasAllCities) return -1;
      if (!aHasAllCities && bHasAllCities) return 1;
      return aText.localeCompare(bText);
    });
    return [{ name: name, label: label, value: sortedCities }];
  };

  const facetServiceCitiesArray: SearchResponseFacet[] = getServiceCities(
    facets,
    'state_city_breadcrumbs',
    'State/City Breadcrumbs'
  );

  const getServiceStates = (array: SearchResponseFacet[], name: string, label: string) => {
    const facetServiceStates = array.filter((a) => a.name === name).map((a) => a.value);
    function removeStateItemsWithSlash(array: SearchResponseFacetItem[]) {
      return array?.filter((item) => {
        return !Object.values(item).some(
          (value) => typeof value === 'string' && value.includes('/')
        );
      });
    }
    const serviceStates = removeStateItemsWithSlash(facetServiceStates[0]);
    return [{ name: name, label: label, value: serviceStates }];
  };

  const facetServiceStatesArray: SearchResponseFacet[] = getServiceStates(
    facets,
    'state_city_breadcrumbs',
    'State/City Breadcrumbs'
  );

  const handleServiceState = (state: string) => {
    setServiceState(state);
    setCityKey((prevKey: number) => prevKey + 1);

    if (serviceCity !== CitySelectText?.value) {
      setServiceCity(CitySelectText?.value as string);
    }

    // Simplified logic: check if state is default/placeholder
    const isDefaultState = state === StateSelectText?.value;
    setSelectCityDisabled(isDefaultState);
    setCityFacetValidation(false);

    if (!isDefaultState) {
      setStateFacetValidation(false);
    }
  };

  const handleServiceCity = (
    city: {
      label: string;
      value: string;
      key: number;
    },
    searchResponseFacet: SearchResponseFacetItem[],
    name: string
  ) => {
    setServiceCity(city.label);
    if (city.value !== CitySelectText?.value) {
      setCityFacetValidation(false);
    }
    const facetValuetId = serviceState + '/' + city.value;
    const facetValue = searchResponseFacet.find((x) => x.text === facetValuetId);
    setCityFacet({
      facetId: name,
      facetIndex: 0,
      facetValueId: facetValue?.id ?? '',
      facetValueIndex: 0,
      checked: true,
      type: 'valueId',
    });
    setServiceCity(city.value);
  };

  const { onClearFilters } = useSearchResultsActions();

  const handleSearchButton = (e: MouseEvent<Element, globalThis.MouseEvent>) => {
    e.preventDefault();
    if (serviceState === StateSelectText?.value) {
      setStateFacetValidation(true);
      setSelectCityDisabled(true);
      return;
    }
    if (serviceCity === CitySelectText?.value) {
      setCityFacetValidation(true);
      return;
    }
    onClearFilters();
    if (cityFacet) {
      onFacetClick(cityFacet);
    }
    setShowSearchResults(true);
  };

  useEnsureFacetUrl(facets);

  useEffect(() => {
    const hasWindow = typeof window !== 'undefined';
    const hash = hasWindow ? window.location.hash.replace(/^#+/, '') : '';

    if (!hash) return;

    const facetsFromUrl = urlToFacet(hash);
    const stateBreadcrumb = facetsFromUrl[0]?.facetValueText?.split('/')[0] || '';
    const cityBreadcrumb = facetsFromUrl[0]?.facetValueText?.split('/')[1] || '';

    handleServiceState(stateBreadcrumb);
    setServiceCity(cityBreadcrumb);

    const facetValue = facets
      .find((a) => a.name === 'state_city_breadcrumbs')
      ?.value.find((x) => x.text === facetsFromUrl[0]?.facetValueText);

    if (!facetValue) {
      setServiceState('Select State');
      return;
    }

    const urlCityFacet = {
      facetId: 'state_city_breadcrumbs',
      facetIndex: 0,
      facetValueId: facetValue?.id || '',
      facetValueIndex: 0,
      checked: true,
      type: 'valueId' as 'valueId' | 'text',
    };

    if (cityFacet !== urlCityFacet) {
      setCityFacet(urlCityFacet);
      onFacetClick(urlCityFacet);
      setShowSearchResults(true);
    }
  }, []);

  const [inputState, setInputState] = useState('');

  const handleStateChange = (value: string) => {
    const modifiedValue = value.replace(/[^a-zA-Z\s]/g, '').slice(0, 2);
    setInputState(modifiedValue);
    return modifiedValue;
  };

  return (
    <div className="w-full flex flex-col md:flex-row gap-8 lg:w-3/4">
      <div className="flex flex-col md:flex-row w-full gap-6 md:gap-4">
        <div className="flex flex-col w-full md:w-1/3">
          <div className="c1 text-light-fill-primary-variant">{StateLabel?.value}</div>

          {facetServiceStatesArray.map((f) => (
            <div data-id="mb-state-dropdown" key={f.name}>
              <Select
                options={f.value?.map((fv, index) => {
                  return {
                    label: fv.text,
                    value: fv.text,
                    key: index,
                  };
                })}
                placeholder={serviceState}
                onChange={(state) => handleServiceState(state?.value || '')}
                aria-labelledby="Select State"
                unstyled // Remove all non-essential styles
                classNames={{
                  container: ({ isDisabled, isFocused }) => {
                    let borderClass = 'border-light-stroke-primary-high';
                    if (isDisabled) {
                      borderClass = 'border-light-stroke-primary-lowest';
                    } else if (isFocused) {
                      borderClass = 'border-light-stroke-primary-variant';
                    }
                    return classNames('border-b-2 w-full py-0.5', borderClass);
                  },
                  dropdownIndicator: ({ isDisabled, isFocused }) => {
                    let indicatorClass = '';
                    if (isDisabled) {
                      indicatorClass = 'text-light-fill-primary-lowest';
                    } else if (isFocused) {
                      indicatorClass = 'text-light-fill-secondary';
                    } else {
                      indicatorClass = 'text-light-fill-primary-variant';
                    }
                    return classNames(indicatorClass, 'p-2');
                  },
                  menu: () =>
                    classNames(
                      'bg-light-background-neutral-variant',
                      'rounded',
                      'shadow-[0_2px_10px_0px_rgba(0,0,0,0.1)]',
                      'my-1'
                    ),
                  menuList: () => classNames('py-1 bok-scrollbar'),
                  option: ({ isSelected, isFocused }) =>
                    classNames(
                      isSelected
                        ? 'bg-light-background-neutral font-medium text-light-fill-primary'
                        : 'bg-transparent text-light-fill-primary-variant',
                      'py-2 px-3 hover:underline',
                      isFocused ? 'underline' : ''
                    ),

                  placeholder: ({ isDisabled }) =>
                    classNames('p2', 'mx-0.5', isDisabled ? 'text-light-fill-primary-low' : ''),
                }}
                inputValue={inputState}
                onInputChange={handleStateChange}
              />
            </div>
          ))}

          {stateFacetValidation && (
            <div className="flex flex-row gap-x-2 mt-2">
              <SVG
                svg="icon-error"
                className="[&_svg]:w-5 [&_svg]:h-5 [&_path]:fill-light-fill-feedback-negative"
              ></SVG>
              <p className="c1 text-light-fill-feedback-negative">Select state.</p>
            </div>
          )}
        </div>
        <div data-id="mb-city-dropdown" className="flex flex-col w-full md:w-3/4">
          <div
            className={`c1 text-light-fill-primary-variant ${
              selectCityDisabled ? 'text-light-fill-primary-low' : ''
            }`}
          >
            {CityLabel?.value}
          </div>
          {facetServiceCitiesArray.map((f) => {
            const options = f.value?.map((fv, index) => {
              return {
                label: fv.text.split('/')[1],
                value: fv.text.split('/')[1],
                key: index,
              };
            });

            return (
              <Select
                options={options as unknown as OptionsOrGroups<string, GroupBase<string>>}
                placeholder={CitySelectText?.value || 'Select One'}
                key={cityKey}
                //eslint-disable-next-line @typescript-eslint/no-explicit-any
                onChange={(city) => handleServiceCity(city as any, f.value, f.name)}
                aria-labelledby="Select City"
                unstyled // Remove all non-essential styles
                classNames={{
                  container: ({ isDisabled, isFocused }) =>
                    classNames(
                      'border-b-2 w-full py-0.5',
                      isDisabled
                        ? 'border-light-stroke-primary-lowest'
                        : isFocused
                        ? 'border-light-stroke-primary-variant'
                        : 'border-light-stroke-primary-high'
                    ),
                  dropdownIndicator: ({ isDisabled, isFocused }) =>
                    classNames(
                      isDisabled
                        ? 'text-light-fill-primary-lowest'
                        : isFocused
                        ? 'text-light-fill-secondary'
                        : 'text-light-fill-primary-variant',
                      'p-2'
                    ),
                  menu: () =>
                    classNames(
                      'bg-light-background-neutral-variant',
                      'rounded',
                      'shadow-[0_2px_10px_0px_rgba(0,0,0,0.1)]',
                      'my-1'
                    ),
                  menuList: () => classNames('py-1'),
                  option: ({ isSelected, isFocused }) =>
                    classNames(
                      isSelected
                        ? 'bg-light-background-neutral font-medium text-light-fill-primary'
                        : 'bg-transparent text-light-fill-primary-variant',
                      'py-2 px-3 hover:underline',
                      isFocused ? 'underline' : ''
                    ),
                  placeholder: ({ isDisabled, isFocused }) =>
                    classNames(
                      'p2',
                      'mx-0.5',
                      isDisabled
                        ? 'text-light-fill-primary-low'
                        : isFocused
                        ? 'text-light-fill-primary-variant'
                        : ''
                    ),
                }}
                isDisabled={selectCityDisabled}
              />
            );
          })}
          {cityFacetValidation && serviceState && (
            <div className="flex flex-row gap-x-2 mt-2">
              <SVG
                svg="icon-error"
                className="[&_svg]:w-5 [&_svg]:h-5 [&_path]:fill-light-fill-feedback-negative"
              ></SVG>
              <p className="c1 text-light-fill-feedback-negative">
                Select the city closest to the property.
              </p>
            </div>
          )}
        </div>
      </div>
      <div className="block mt-0 md:mt-4">
        <Button
          type="default"
          title="Search"
          label={SearchLabel?.value || 'Search'}
          onClick={(e) => handleSearchButton(e)}
          aria-labelledby="Search Button for Banker using City and State"
        />
      </div>
    </div>
  );
};

type ArticleModel = {
  id: string;
  type?: string;
  title?: string;
  name?: string;
  subtitle?: string;
  url?: string;
  description?: string;
  content_text?: string;
  image_url?: string;
  source_id?: string;
  first_name?: string;
  last_name?: string;
  job_title?: string;
  nmls?: string;
  email?: string;
  address?: string;
  city?: string;
  state?: string;
  zipcode?: string;
};

type ArticleSearchResultsProps = {
  defaultSortType?: SearchResultsStoreState['sortType'];
  defaultPage?: SearchResultsStoreState['page'];
  defaultItemsPerPage?: SearchResultsStoreState['itemsPerPage'];
  defaultKeyphrase?: SearchResultsStoreState['keyphrase'];
  props?: BankerSearchProps;
  facetsFromUrl?: FacetValue[];
};

type InitialState = SearchResultsInitialState<'itemsPerPage' | 'keyphrase' | 'page' | 'sortType'>;

const searchSource = process.env.BANKER_SEARCH_SOURCE_ID;
const searchSourceIds = searchSource?.split('|') || [];

export const BankerProfileSearchComponent = ({
  defaultSortType = 'featured_desc',
  defaultPage = 1,
  defaultKeyphrase = '',
  defaultItemsPerPage = 24,
  props,
}: ArticleSearchResultsProps) => {
  const {
    sanitizedResults,
    widgetRef,
    actions: { onItemClick },
    queryResult: { isLoading, isFetching, data: { facet: facets = [] } = {} },
  } = useSanitizedSearchResults<ArticleModel, InitialState>({
    query: (query) => {
      query
        .getRequest()
        .setSources(searchSourceIds)
        .setSearchFacetTypes([{ name: 'state_city_breadcrumbs' }])
        .setSearchFacetAll(false);
    },
    state: () => {
      const hasWindow = typeof window !== 'undefined';
      const hash = hasWindow ? window.location.hash.replace(/^#+/, '') : '';
      const facetsFromUrl = urlToFacet(hash);

      return {
        sortType: defaultSortType,
        page: defaultPage,
        itemsPerPage: defaultItemsPerPage,
        keyphrase: defaultKeyphrase,
        selectedFacets: facetsFromUrl,
      };
    },
  });

  const { Title, NamePlaceholder, ResultsNote } = props?.fields || {};

  const [showSearchResults, setShowSearchResults] = useState<boolean>();
  const [searchInput, setSearchInput] = useState<string>();

  const dict = useDictionary();
  const dictNoResults =
    dict?.['Feature.Profiles.BankerSearch.No.Results'] || 'Your search returned no results.';

  const refSearchInput = useRef<HTMLInputElement>(null);

  const filteredArticles = sanitizedResults?.content?.filter(
    (x) => !searchInput || x.name?.toUpperCase().indexOf(searchInput?.toUpperCase().trim()) !== -1
  );

  const keyphraseChangeFn = (e: { target: { value: SetStateAction<string | undefined> } }) => {
    setSearchInput(e.target.value);
  };

  const shuffleBankerProfiles = (array: ArticleModel[]) => {
    let currentIndex = array.length;
    while (currentIndex != 0) {
      const randomIndex = Math.floor(Math.random() * currentIndex);
      currentIndex--;
      [array[currentIndex], array[randomIndex]] = [array[randomIndex], array[currentIndex]];
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  sanitizedResults?.content && shuffleBankerProfiles(sanitizedResults?.content);

  const handleClearSearchInput = (e: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>) => {
    e.preventDefault();
    refSearchInput.current?.focus();
    setSearchInput('');
  };

  useEffect(() => {
    refSearchInput.current?.focus();
  });

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen w-full bg-light-background-neutral">
        <Spinner loading />
      </div>
    );
  }

  return (
    <section className="container mt-6 lg:mt-8 " ref={widgetRef}>
      <div
        className="p-4 md:p-8 lg:p-10 bg-light-background-neutral-variant shadow-lg"
        ref={widgetRef}
      >
        {isFetching && (
          <div className="flex justify-center items-center h-screen w-full bg-light-background-neutral-variant relative">
            <div className="absolute top-[50%] left-[50%] -translate-x-[50%] -translate-y-[50%] flex flex-col justify-center items-center z-40">
              <Spinner loading />
            </div>
          </div>
        )}
        <section className="flex flex-col">
          <div className="flex flex-col gap-8">
            <h4 className="w-full s1-medium">{Title?.value}</h4>
            <SearchFacetsServiceLocations
              setShowSearchResults={setShowSearchResults}
              facets={facets}
              props={props}
            />
          </div>
          <div className="flex flex-row gap-0 md:gap-4 mt-8">
            <div className="w-full block relative my-2 md:my-4 lg:w-5/12 p2-medium">
              <input
                value={searchInput}
                ref={refSearchInput}
                onChange={(e) => {
                  keyphraseChangeFn(e);
                  setSearchInput(e.target.value);
                }}
                className={`peer border ${
                  showSearchResults
                    ? 'border-light-stroke-primary-high placeholder:text-light-fill-primary-variant'
                    : 'stroke-light-stroke-primary-lowest placeholder:text-light-fill-primary-low cursor-not-allowed'
                } rounded-full w-full p-2 pl-5 pr-12 bg-light-background-neutral-variant focus:outline-light-stroke-primary-variant placeholder:p3 p3-medium`}
                placeholder={NamePlaceholder?.value}
                disabled={!showSearchResults}
              />
              <div
                className={`absolute top-0 right-[46px] bottom-0 ${
                  searchInput?.length && searchInput !== '' ? '' : 'hidden'
                }`}
              >
                <button
                  type="button"
                  onClick={(e) => handleClearSearchInput(e)}
                  className="flex items-center justify-center w-[40px] h-full stroke-light-stroke-primary-high"
                >
                  <span className="sr-only">Clear Search Input</span>
                  <SVG svg="icon-close" hidden={false} className="[&_svg]:w-3 [&_svg]:h-3"></SVG>
                </button>
              </div>
              <div className="absolute top-0 right-0 bottom-0">
                <button
                  type="button"
                  onClick={(e) => e.preventDefault}
                  className={`flex items-center justify-center w-[46px] h-full rounded-r-full ${
                    showSearchResults
                      ? 'bg-light-background-gradient hover:bg-light-background-gradient-active'
                      : 'bg-light-background-neutral-high hover:bg-light-background-neutral-high cursor-not-allowed'
                  } text-light-fill-inverse peer-focus:text-light-fill-secondary`}
                  disabled={!showSearchResults}
                >
                  <span className="sr-only">Search</span>
                  <SVG svg="icon-search" hidden={false} className="[&_svg]:w-5 [&_svg]:h-5"></SVG>
                </button>
              </div>
            </div>
            <div className="hidden lg:w-full md:flex items-center ">
              <div className="flex-grow border-t border-light-stroke-primary-high"></div>
            </div>
          </div>
          <section className="flex flex-col mt-8">
            <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-5 justify-center">
              {showSearchResults &&
                filteredArticles?.map((a, index) => (
                  <ArticleHorizontalItemCard
                    key={a.id}
                    article={a}
                    index={index}
                    onItemClick={onItemClick}
                    displayText={true}
                  />
                ))}
            </div>
          </section>
        </section>
        {filteredArticles && filteredArticles.length <= 0 && !isFetching && (
          <p className="flex justify-center w-full s2-medium">{dictNoResults}</p>
        )}
        {!showSearchResults && !isFetching && (
          <p className="p2 text-light-fill-primary-variant flex justify-center w-full">
            {ResultsNote?.value}
          </p>
        )}
      </div>
    </section>
  );
};

const BankerProfileSearchWidget = widget(
  BankerProfileSearchComponent,
  WidgetDataType.SEARCH_RESULTS,
  'content'
);

export default BankerProfileSearchWidget;
